/**
 * Order Service
 *
 * Handles all order-related API calls
 */

import api from './api';

export const orderService = {
  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @param {Array} orderData.items - Cart items
   * @param {Object} orderData.shippingAddress - Shipping address
   * @param {Object} orderData.billingAddress - Billing address (optional)
   * @param {string} orderData.paymentMethod - Payment method (card, paypal)
   * @param {boolean} orderData.isGift - Whether order is a gift
   * @param {Object} orderData.giftMessage - Gift message (optional)
   * @returns {Promise<Object>} Created order with payment URL
   */
  createOrder: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data;
    } catch (error) {
      console.error('[OrderService] Create order error:', error);
      throw error;
    }
  },

  /**
   * Get all orders for current user
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   * @returns {Promise<Object>} Orders with pagination
   */
  getOrders: async (params = {}) => {
    try {
      const response = await api.get('/orders', { params });
      return response.data;
    } catch (error) {
      console.error('[OrderService] Get orders error:', error);
      throw error;
    }
  },

  /**
   * Get a single order by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  getOrder: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('[OrderService] Get order error:', error);
      throw error;
    }
  },

  /**
   * Get order by order number
   * @param {string} orderNumber - Order number
   * @returns {Promise<Object>} Order details
   */
  getOrderByNumber: async (orderNumber) => {
    try {
      const response = await api.get(`/orders/${orderNumber}`);
      return response.data;
    } catch (error) {
      console.error('[OrderService] Get order by number error:', error);
      throw error;
    }
  },

  /**
   * Track order
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order tracking information
   */
  trackOrder: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      if (response.data.success && response.data.data) {
        const order = response.data.data;
        return {
          success: true,
          data: {
            orderNumber: order.orderNumber,
            status: order.status,
            tracking: order.tracking,
            timeline: order.timeline,
            estimatedDelivery: order.estimatedDelivery
          }
        };
      }
      return response.data;
    } catch (error) {
      console.error('[OrderService] Track order error:', error);
      throw error;
    }
  },

  /**
   * Get recent orders
   * @param {number} limit - Number of orders to fetch
   * @returns {Promise<Object>} Recent orders
   */
  getRecentOrders: async (limit = 5) => {
    try {
      const response = await api.get('/orders', {
        params: { limit, page: 1 }
      });
      return response.data;
    } catch (error) {
      console.error('[OrderService] Get recent orders error:', error);
      throw error;
    }
  }
};

export default orderService;
